/**
 * Screening-grade steep-slope exclusion zones from sampled elevations.
 *
 * Pure and deterministic: takes the elevation samples the existing cascade
 * already produces (Google Elevation / USGS EPQS grid over the parcel),
 * grades each sample against its nearest neighbours, and turns steep samples
 * into square no-build cells unioned and clipped to the parcel. Cell size is
 * the median neighbour spacing, so adjacent steep cells merge into contiguous
 * zones. This is a coarse constructability screen — grading design, cut/fill
 * and geotech remain external.
 */
import polygonClipping from 'polygon-clipping';

import type { GenerationLayoutExclusionZone } from '../../types/generationImpact';

const EARTH_RADIUS_M = 6_378_137;
const MIN_COS_LATITUDE = 1e-8;
const DEFAULT_GRADE_THRESHOLD_PCT = 8;
const NEIGHBOURS_PER_SAMPLE = 4;
const MIN_SAMPLES = 4;
/** Slightly oversized cells so orthogonally adjacent steep cells union. */
const CELL_OVERLAP_FACTOR = 1.05;
const COORDINATE_DECIMALS = 7;

export interface SlopeElevationSample {
  lat: number;
  lng: number;
  elevationMeters: number;
}

export interface DeriveSteepSlopeExclusionZonesInput {
  samples: readonly SlopeElevationSample[];
  parcelPolygon: ReadonlyArray<{ lat: number; lng: number }>;
  /** Grade above which ground is excluded. Default 8%. */
  gradeThresholdPct?: number;
  /** Provenance recorded on the derived zone. */
  source: string;
  retrievedAt: string;
}

export interface DeriveSteepSlopeExclusionZonesResult {
  status: 'derived' | 'insufficient-samples';
  zones: GenerationLayoutExclusionZone[];
  sampleCount: number;
  steepSampleCount: number;
  maxGradePct: number | null;
  gradeThresholdPct: number;
}

interface LocalSample extends SlopeElevationSample {
  xM: number;
  yM: number;
  maxGradePct: number;
}

function roundCoordinate(value: number): number {
  const scale = 10 ** COORDINATE_DECIMALS;
  return Math.round(value * scale) / scale;
}

function median(values: number[]): number {
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1]! + sorted[middle]!) / 2
    : sorted[middle]!;
}

export function deriveSteepSlopeExclusionZones(
  input: DeriveSteepSlopeExclusionZonesInput,
): DeriveSteepSlopeExclusionZonesResult {
  const gradeThresholdPct = input.gradeThresholdPct ?? DEFAULT_GRADE_THRESHOLD_PCT;
  const samples = input.samples.filter(sample => (
    Number.isFinite(sample.lat) && Number.isFinite(sample.lng)
    && Number.isFinite(sample.elevationMeters)
  ));
  if (samples.length < MIN_SAMPLES) {
    return {
      status: 'insufficient-samples',
      zones: [],
      sampleCount: samples.length,
      steepSampleCount: 0,
      maxGradePct: null,
      gradeThresholdPct,
    };
  }

  const reference = samples[0]!;
  const cosLat = Math.max(
    MIN_COS_LATITUDE,
    Math.abs(Math.cos(reference.lat * Math.PI / 180)),
  );
  const toLocal = (point: { lat: number; lng: number }) => ({
    xM: (point.lng - reference.lng) * Math.PI / 180 * EARTH_RADIUS_M * cosLat,
    yM: (point.lat - reference.lat) * Math.PI / 180 * EARTH_RADIUS_M,
  });

  const local: LocalSample[] = samples.map(sample => ({
    ...sample,
    ...toLocal(sample),
    maxGradePct: 0,
  }));

  const nearestSpacings: number[] = [];
  for (const sample of local) {
    const neighbours = local
      .filter(candidate => candidate !== sample)
      .map(candidate => ({
        candidate,
        distanceM: Math.hypot(candidate.xM - sample.xM, candidate.yM - sample.yM),
      }))
      .filter(entry => entry.distanceM > 0)
      .sort((left, right) => left.distanceM - right.distanceM)
      .slice(0, NEIGHBOURS_PER_SAMPLE);
    if (neighbours.length === 0) continue;
    nearestSpacings.push(neighbours[0]!.distanceM);
    sample.maxGradePct = Math.max(...neighbours.map(entry => (
      Math.abs(entry.candidate.elevationMeters - sample.elevationMeters)
        / entry.distanceM * 100
    )));
  }

  const maxGradePct = local.length > 0
    ? Math.round(Math.max(...local.map(sample => sample.maxGradePct)) * 10) / 10
    : null;
  const steep = local.filter(sample => sample.maxGradePct > gradeThresholdPct);
  if (steep.length === 0 || nearestSpacings.length === 0) {
    return {
      status: 'derived',
      zones: [],
      sampleCount: samples.length,
      steepSampleCount: 0,
      maxGradePct,
      gradeThresholdPct,
    };
  }

  const halfCellM = median(nearestSpacings) * CELL_OVERLAP_FACTOR / 2;
  const dLat = halfCellM / EARTH_RADIUS_M * 180 / Math.PI;
  const dLng = halfCellM / (EARTH_RADIUS_M * cosLat) * 180 / Math.PI;

  const cellRing = (sample: LocalSample): polygonClipping.Ring => [
    [sample.lng - dLng, sample.lat - dLat],
    [sample.lng + dLng, sample.lat - dLat],
    [sample.lng + dLng, sample.lat + dLat],
    [sample.lng - dLng, sample.lat + dLat],
    [sample.lng - dLng, sample.lat - dLat],
  ];

  let merged: polygonClipping.MultiPolygon;
  try {
    merged = polygonClipping.union([cellRing(steep[0]!)]);
    for (let index = 1; index < steep.length; index += 1) {
      merged = polygonClipping.union(merged, [cellRing(steep[index]!)]);
    }
    const parcelRing = input.parcelPolygon
      .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
      .map(point => [point.lng, point.lat] as [number, number]);
    if (parcelRing.length >= 3) {
      const first = parcelRing[0]!;
      const last = parcelRing.at(-1)!;
      if (first[0] !== last[0] || first[1] !== last[1]) parcelRing.push([...first]);
      merged = polygonClipping.intersection(merged, [parcelRing]);
    }
  } catch {
    return {
      status: 'derived',
      zones: [],
      sampleCount: samples.length,
      steepSampleCount: steep.length,
      maxGradePct,
      gradeThresholdPct,
    };
  }

  const polygons = merged
    .map(polygon => polygon[0])
    .filter((outer): outer is polygonClipping.Ring => outer != null && outer.length >= 4)
    .map(outer => outer.map(([lng, lat]) => ({
      lat: roundCoordinate(lat),
      lng: roundCoordinate(lng),
    })))
    .sort((left, right) => left[0]!.lat - right[0]!.lat || left[0]!.lng - right[0]!.lng);

  const zones: GenerationLayoutExclusionZone[] = polygons.length === 0
    ? []
    : [{
        id: 'derived-steep-slope',
        label: `Derived steep slope > ${gradeThresholdPct}% (screening, ${input.source})`,
        source: 'derived-slope',
        kind: 'steep-slope',
        polygons,
        provenance: {
          providerId: 'derived-slope',
          retrievedAt: input.retrievedAt,
        },
      }];

  return {
    status: 'derived',
    zones,
    sampleCount: samples.length,
    steepSampleCount: steep.length,
    maxGradePct,
    gradeThresholdPct,
  };
}
